import React, {
  createContext,
  type Dispatch,
  type MutableRefObject,
  useContext,
  useReducer,
  useRef,
} from 'react';
import {shallowEqual} from 'react-redux';

import type {AgentChatAction} from './actions';
import {agentChatReducer} from './reducer';
import type {ThreadListItem} from './selectors';
import {
  selectActiveThread,
  selectActiveThreadId,
  selectThreadListItems,
} from './selectors';
import type {AgentChatState, Thread} from './thread';
import {createDefaultState} from './thread';

// Action dispatch

const AgentChatDispatchContext = createContext<Dispatch<AgentChatAction>>(
  () => {}
);

export function useAgentChatDispatch(): Dispatch<AgentChatAction> {
  return useContext(AgentChatDispatchContext);
}

// Thread list

interface ThreadListContextValue {
  items: ThreadListItem[];
  activeThreadId: string | null;
}

const ThreadListContext = createContext<ThreadListContextValue>({
  items: [],
  activeThreadId: null,
});

export function useThreadList(): ThreadListContextValue {
  return useContext(ThreadListContext);
}

// Active thread

const ActiveThreadContext = createContext<Thread | undefined>(undefined);

export function useActiveThread(): Thread | undefined {
  return useContext(ActiveThreadContext);
}

// State ref

/**
 * Holds a ref to the full AgentChatState so the client-side runtime's message
 * getter can read any thread without subscribing to re-renders.
 *
 * TODO: This context exists only for the client-side runtime's message getter.
 * Remove it when migrating to the backend runtime.
 */
const AgentChatStateRefContext = createContext<
  MutableRefObject<AgentChatState>
>({current: createDefaultState()});

export function useAgentChatStateRef(): MutableRefObject<AgentChatState> {
  return useContext(AgentChatStateRefContext);
}

// Provider

export function AgentChatStoreProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [state, dispatch] = useReducer(agentChatReducer, createDefaultState());

  const stateRef = useRef(state);
  stateRef.current = state;

  const threadListValue = useStableThreadList(state);
  const activeThread = selectActiveThread(state);

  return (
    <AgentChatDispatchContext.Provider value={dispatch}>
      <AgentChatStateRefContext.Provider value={stateRef}>
        <ThreadListContext.Provider value={threadListValue}>
          <ActiveThreadContext.Provider value={activeThread}>
            {children}
          </ActiveThreadContext.Provider>
        </ThreadListContext.Provider>
      </AgentChatStateRefContext.Provider>
    </AgentChatDispatchContext.Provider>
  );
}

/**
 * useStableThreadList provides a memoized thread-list context so consumers only
 * re-render when list-visible fields (id, title, status) actually change.
 */
function useStableThreadList(state: AgentChatState): ThreadListContextValue {
  const items = selectThreadListItems(state);
  const activeThreadId = selectActiveThreadId(state);
  const prevRef = useRef<ThreadListContextValue>({items, activeThreadId});

  const itemsChanged =
    items.length !== prevRef.current.items.length ||
    items.some((item, i) => !shallowEqual(item, prevRef.current.items[i]));
  const activeChanged = activeThreadId !== prevRef.current.activeThreadId;

  if (itemsChanged || activeChanged) {
    prevRef.current = {items, activeThreadId};
  }

  return prevRef.current;
}
